import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabDescriptor, CommandeBC } from '../types';
import { buildCommandeTabs, computeDefaultTabIndex } from '../domain/commandeTabs';
import { buildTabParam, resolveTabParam } from '../../../utils/tabPreference';

// Hook mince : la logique (gating / onglet par defaut) vit dans domain/commandeTabs.
// Ici, uniquement le cablage de l'onglet actif a l'URL (?tab=<index>), comme la case acheteur.
export const useCommandeTabs = (commande: CommandeBC | null) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const descriptors = useMemo<TabDescriptor[]>(
    () => (commande ? buildCommandeTabs(commande) : []),
    [commande],
  );

  const defaultIndex = useMemo(
    () => (commande ? computeDefaultTabIndex(descriptors, commande) : 0),
    [descriptors, commande],
  );

  const tabParam = searchParams.get('tab');
  // ?tab= porte une LISTE de preferences (index, cle ou kind), resolue par utils/tabPreference avec
  // la meme garde disabled/inactive que la case acheteur : on ne route pas vers un onglet grise.
  const activeIndex = useMemo(() => {
    const resolved = resolveTabParam(tabParam, descriptors);
    return resolved >= 0 ? resolved : defaultIndex;
  }, [tabParam, defaultIndex, descriptors]);

  const setActiveIndex = useCallback(
    (index: number) => {
      const target = descriptors[index];
      if (!target || target.disabled || target.inactive) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', String(index));
      setSearchParams(next, { replace: true });
    },
    [descriptors, searchParams, setSearchParams],
  );

  // Destination DECLAREE, sans garde immediate : contrairement a setActiveIndex, on n'exige pas que
  // la cible soit ouverte MAINTENANT. Appele apres une signature, l'etat en memoire est encore celui
  // d'avant le refetch ; l'arbitrage se fait au render suivant, sur des descripteurs frais.
  const requestTab = useCallback(
    (preferences: string[]) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', buildTabParam(preferences));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return {
    descriptors,
    activeIndex,
    activeTab: descriptors[activeIndex] ?? null,
    setActiveIndex,
    requestTab,
  };
};
